import { useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, Clock, MapPin, ArrowUpRight } from 'lucide-react';
import { getCategoryBySlug, getSubCategoryBySlug, serviceCategories } from '../data/servicesData';
import BookingModal from '../components/BookingModal';

const ServiceSubCategory = () => {
  const { categorySlug, subCategorySlug } = useParams();
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  
  const category = getCategoryBySlug(categorySlug);
  const subCategory = getSubCategoryBySlug(subCategorySlug);

  if (!category || !subCategory) {
    return (
      <main className="bg-black min-h-screen pt-32">
        <Helmet>
          <title>Service Not Found | BLR Nail Club</title>
        </Helmet>
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h1 className="text-3xl text-white mb-4">Service Not Found</h1>
          <Link to="/services" className="text-[#B8860B] hover:underline">
            ← Back to Services
          </Link>
        </div>
      </main>
    );
  }

  const shareUrl = `https://www.blrnailclub.in/services/${categorySlug}/${subCategorySlug}`;
  
  // Get related subcategories from same category
  const relatedSubCategories = category.subCategories
    .filter(s => s.slug !== subCategorySlug)
    .slice(0, 4);

  return (
    <>
      <Helmet>
        <title>{`${subCategory.name} in Bangalore | BLR Nail Club - ${category.name} Services`}</title>
        <meta name="description" content={`${subCategory.description}. Best ${subCategory.name.toLowerCase()} services at affordable prices in Bangalore. Available at RR Nagar, Basaveshwarnagar & CSK Mall Varthur.`} />
        <meta name="keywords" content={`${subCategory.name.toLowerCase()} Bangalore, ${subCategory.name.toLowerCase()} RR Nagar, ${subCategory.name.toLowerCase()} near me, ${category.name.toLowerCase()} services`} />
        <link rel="canonical" href={shareUrl} />
        <meta property="og:title" content={`${subCategory.name} | BLR Nail Club Bangalore`} />
        <meta property="og:description" content={subCategory.description} />
        <meta property="og:image" content={subCategory.image} />
        <meta property="og:url" content={shareUrl} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": subCategory.name,
            "description": subCategory.description,
            "provider": {
              "@type": "LocalBusiness",
              "name": "BLR Nail Club",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "N2VK Arcade, 1154, Jawaharlal Nehru Rd",
                "addressLocality": "Rajarajeshwari Nagar",
                "addressRegion": "Bangalore",
                "postalCode": "560098"
              }
            },
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": subCategory.name,
              "itemListElement": subCategory.services.map((service, idx) => ({
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": service.name,
                  "offers": {
                    "@type": "Offer",
                    "price": service.price.replace('Rs. ', '').replace(',', ''),
                    "priceCurrency": "INR"
                  }
                }
              }))
            },
            "image": subCategory.image
          })}
        </script>
      </Helmet>

      <main className="bg-black min-h-screen" data-testid="service-subcategory-page">
        {/* Hero Section */}
        <div className="relative h-[45vh] md:h-[55vh]">
          <img
            src={subCategory.image}
            alt={subCategory.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/30"></div>
          
          {/* Breadcrumb */}
          <div className="absolute top-28 left-6 md:left-12 z-10">
            <div className="flex items-center gap-2 text-sm">
              <Link to="/services" className="text-white/50 hover:text-[#B8860B] transition-colors">
                Services
              </Link>
              <span className="text-white/30">/</span>
              <Link to={`/services/${categorySlug}`} className="text-white/50 hover:text-[#B8860B] transition-colors">
                {category.name}
              </Link>
              <span className="text-white/30">/</span>
              <span className="text-[#B8860B]">{subCategory.name}</span>
            </div>
          </div>

          {/* Hero Content */}
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-3 mb-4">
                <span className="bg-[#B8860B] text-black px-3 py-1 text-xs tracking-wider uppercase">
                  {category.name}
                </span>
                {subCategory.trending && (
                  <span className="bg-white/10 text-[#B8860B] px-3 py-1 text-xs tracking-wider border border-[#B8860B]/30">
                    Trending
                  </span>
                )}
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-light text-white mb-4">
                {subCategory.name}
              </h1>
              <p className="text-white/70 text-lg md:text-xl max-w-2xl">
                {subCategory.description}
              </p>
            </div>
          </div>
        </div>

        {/* Services Price List */}
        <section className="py-12 md:py-16 bg-[#0a0a0a]">
          <div className="max-w-6xl mx-auto px-6 md:px-12">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl md:text-3xl font-light text-white">
                  {subCategory.name} Services
                </h2>
                <p className="text-white/50 text-sm mt-2">
                  {subCategory.services.length} treatment{subCategory.services.length > 1 ? 's' : ''} available
                </p>
              </div>
              <button
                onClick={() => setIsBookingModalOpen(true)}
                className="hidden md:flex items-center gap-2 bg-[#B8860B] text-black px-6 py-3 text-sm tracking-wider hover:bg-[#9A7B0A] transition-colors"
              >
                Book Now
                <ArrowUpRight size={16} />
              </button>
            </div>

            {/* Services Table/Cards */}
            <div className="space-y-3">
              {subCategory.services.map((service, index) => (
                <div
                  key={index}
                  className="group bg-black border border-white/10 p-5 md:p-6 hover:border-[#B8860B]/40 transition-all duration-300"
                  data-testid={`service-item-${index}`}
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1">
                      <h3 className="text-white text-lg md:text-xl font-light tracking-wide group-hover:text-[#B8860B] transition-colors">
                        {service.name}
                      </h3>
                      <div className="flex items-center gap-4 mt-2 text-white/50 text-sm">
                        <span className="flex items-center gap-1.5">
                          <Clock size={14} className="text-[#B8860B]/70" />
                          {service.duration}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between md:justify-end gap-6">
                      <div className="text-right">
                        <span className="text-[#B8860B] text-xl md:text-2xl font-light">{service.price}</span>
                      </div>
                      <button
                        onClick={() => setIsBookingModalOpen(true)}
                        className="px-5 py-2.5 border border-[#B8860B] text-[#B8860B] text-sm tracking-wider hover:bg-[#B8860B] hover:text-black transition-colors"
                      >
                        Book
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Mobile Book Now */}
            <div className="mt-8 md:hidden">
              <button
                onClick={() => setIsBookingModalOpen(true)}
                className="w-full bg-[#B8860B] text-black py-4 text-sm tracking-[0.15em] uppercase font-medium hover:bg-[#9A7B0A] transition-colors"
              >
                Book Appointment
              </button>
            </div>
          </div>
        </section>

        {/* Locations */}
        <section className="py-12 bg-black border-t border-white/10">
          <div className="max-w-6xl mx-auto px-6 md:px-12">
            <h2 className="text-xl font-light text-white mb-6">Available At</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Link 
                to="/nails-rr-nagar"
                className="flex items-start gap-3 bg-white/5 border border-white/10 p-4 hover:border-[#B8860B]/30 transition-colors"
              >
                <MapPin className="text-[#B8860B] flex-shrink-0 mt-0.5" size={18} />
                <div>
                  <p className="text-white text-sm font-light">BLR Nail Club - RR Nagar</p>
                  <p className="text-white/40 text-xs mt-1">Rajarajeshwari Nagar</p>
                </div>
              </Link>
              <Link 
                to="/nailashes-basaveshwarnagar"
                className="flex items-start gap-3 bg-white/5 border border-white/10 p-4 hover:border-[#B8860B]/30 transition-colors"
              >
                <MapPin className="text-[#B8860B] flex-shrink-0 mt-0.5" size={18} />
                <div>
                  <p className="text-white text-sm font-light">Nailashes - Basaveshwarnagar</p>
                  <p className="text-white/40 text-xs mt-1">Basaveshwarnagar</p>
                </div>
              </Link>
              <Link 
                to="/nails-gunjur"
                className="flex items-start gap-3 bg-white/5 border border-white/10 p-4 hover:border-[#B8860B]/30 transition-colors"
              >
                <MapPin className="text-[#B8860B] flex-shrink-0 mt-0.5" size={18} />
                <div>
                  <p className="text-white text-sm font-light">CSK Mall - Varthur</p>
                  <p className="text-white/40 text-xs mt-1">Gunjur, Varthur</p>
                </div>
              </Link>
            </div>
          </div>
        </section>

        {/* Related SubCategories */}
        {relatedSubCategories.length > 0 && (
          <section className="py-12 md:py-16 bg-[#0a0a0a] border-t border-white/10">
            <div className="max-w-6xl mx-auto px-6 md:px-12">
              <h2 className="text-xl md:text-2xl font-light text-white mb-6">
                More {category.name} Services
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {relatedSubCategories.map((related) => (
                  <Link
                    key={related.id}
                    to={`/services/${categorySlug}/${related.slug}`}
                    className="group relative overflow-hidden aspect-[4/3]"
                  >
                    <img
                      src={related.image}
                      alt={related.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent"></div>
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <h3 className="text-white text-sm md:text-base font-light group-hover:text-[#B8860B] transition-colors">
                        {related.name}
                      </h3>
                      <p className="text-white/50 text-xs mt-1">
                        {related.services.length} services
                      </p>
                    </div>
                    {related.trending && (
                      <span className="absolute top-3 right-3 w-2 h-2 rounded-full bg-[#B8860B]"></span>
                    )}
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Back Navigation */}
        <section className="py-8 bg-black border-t border-white/10">
          <div className="max-w-6xl mx-auto px-6 md:px-12">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <Link
                to={`/services/${categorySlug}`}
                className="flex items-center gap-2 text-white/60 hover:text-[#B8860B] transition-colors"
              >
                <ArrowLeft size={18} />
                <span className="text-sm">Back to {category.name}</span>
              </Link>
              <Link
                to="/services"
                className="text-sm text-[#B8860B] hover:text-[#9A7B0A] transition-colors"
              >
                Browse All Services →
              </Link>
            </div>
          </div>
        </section>

        {/* Booking Modal */}
        <BookingModal isOpen={isBookingModalOpen} onClose={() => setIsBookingModalOpen(false)} />
      </main>
    </>
  );
};

export default ServiceSubCategory;
